import {createSlice} from '@reduxjs/toolkit';

import quizStatuses from '../../consts/loyalty/quizStatuses';
import storeSliceNames from '../../consts/storeSliceNames';

const initialState = {
    badges: {},
    stories: {},
    quizzes: {},
    conversionStatus: null,
    userData: {},
    rewards: {},
    isBadgesLoaded: false,
};

const loyaltySlice = createSlice({
    name: storeSliceNames.loyalty,
    initialState,
    reducers: {
        setBadges(state, action) {
            return {
                ...state,
                badges: action.payload,
            };
        },
        setCurrentBadge(state, action) {
            return {
                ...state,
                currentBadge: action.payload,
            };
        },
        addMilestoneToBadge(state, action) {
            const {milestoneType, badgeType} = action.payload;

            const completedMilestones = [...state.badges[badgeType].completedMilestones, milestoneType];

            return {
                ...state,
                badges: {
                    ...state.badges,
                    [badgeType]: {
                        ...state.badges[badgeType],
                        completedMilestones,
                        progress: completedMilestones.length,
                        endTs: Date.now(),
                        isCompleted: completedMilestones.length === state.badges[badgeType].milestones.length,
                    },
                },
            };
        },
        addMilestonesToBadge(state, action) {
            const {milestones, badgeType} = action.payload;

            const completedMilestones = [...state.badges[badgeType].completedMilestones, ...milestones];

            return {
                ...state,
                badges: {
                    ...state.badges,
                    [badgeType]: {
                        ...state.badges[badgeType],
                        completedMilestones,
                        progress: completedMilestones.length,
                        isCompleted: completedMilestones.length === state.badges[badgeType].milestones.length,
                    },
                },
            };
        },
        addSelectedRewardTypeToBadge(state, action) {
            const {rewardType, badgeType} = action.payload;

            return {
                ...state,
                badges: {
                    ...state.badges,
                    [badgeType]: {
                        ...state.badges[badgeType],
                        rewardType,
                    },
                },
            };
        },
        setIsLoyaltyMockEnabled(state, action) {
            return {
                ...state,
                isLoyaltyMockEnabled: action.payload,
            };
        },
        startQuiz(state, action) {
            const {quizType} = action.payload;
            const currentQuiz = state?.quizzes?.[quizType] || {};

            return {
                ...state,
                quizzes: {...state.quizzes, [quizType]: {...currentQuiz, status: quizStatuses.IN_PROGRESS}},
            };
        },
        setRestoredQuizData(state, action) {
            const {quizType, quizData} = action.payload;

            return {
                ...state,
                quizzes: {
                    ...state.quizzes,
                    [quizType]: {
                        ...quizData,
                    },
                },
            };
        },
        finishQuiz(state, action) {
            const {quizType} = action.payload;
            const currentQuiz = state?.quizzes?.[quizType] || {};

            return {
                ...state,
                quizzes: {...state.quizzes, [quizType]: {...currentQuiz, status: quizStatuses.COMPLETED}},
            };
        },
        setQuizAnswer(state, {payload: {quizType, questionId, optionId, blankId, isMultipleSelect}}) {
            const {selected} = getOrCreateQuiz(state, quizType);

            if (isMultipleSelect) {
                handleMultipleSelect({selected, questionId, optionId, blankId});
            } else {
                handleSingleSelect({selected, questionId, optionId, blankId});
            }
        },
        setIsAnswersCorrect(state, action) {
            const {quizType, isAnswersCorrect} = action.payload;
            const currentQuiz = state?.quizzes?.[quizType];

            if (!currentQuiz) return;

            currentQuiz.isAnswersCorrect = isAnswersCorrect;
        },
        setIsTimeoutError(state, action) {
            const {quizType, isTimeoutError} = action.payload;
            const currentQuiz = state?.quizzes?.[quizType];

            if (!currentQuiz) return;

            currentQuiz.isTimeoutError = isTimeoutError;
        },
        resetQuiz(state, action) {
            const {quizType} = action.payload;
            const currentQuiz = state?.quizzes?.[quizType] || {};

            if (currentQuiz) {
                currentQuiz.status = quizStatuses.NOT_STARTED;
                currentQuiz.isAnswersCorrect = null;
                currentQuiz.isTimeoutError = null;
                delete currentQuiz.selectedOptions;
            }
        },
        setConversionStatus(state, action) {
            return {
                ...state,
                conversionStatus: action.payload,
            };
        },
        setLoyaltyUserData(state, action) {
            return {
                ...state,
                userData: action.payload,
            };
        },
        setRewards(state, action) {
            return {
                ...state,
                rewards: action.payload,
            };
        },
        setIsBadgesLoaded(state, action) {
            return {
                ...state,
                isBadgesLoaded: action.payload,
            };
        },
        setBadgesIsProgress(state, action) {
            return {
                ...state,
                isBadgesInProgress: action.payload,
            };
        },
    },
});

const getOrCreateQuiz = (state, quizType) => {
    if (!state.quizzes[quizType]) {
        state.quizzes[quizType] = {};
    }
    const quiz = state.quizzes[quizType];

    if (!quiz.selectedOptions) {
        quiz.selectedOptions = {};
    }
    const selected = quiz.selectedOptions;

    return {quiz, selected};
};

const handleMultipleSelect = ({selected, questionId, optionId, blankId}) => {
    let list = selected[questionId];

    if (!Array.isArray(list)) {
        list = [];
        selected[questionId] = list;
    }

    if (blankId) {
        handleBlankOption({list, optionId, blankId});
    } else {
        handleRegularOption({list, optionId, selected, questionId});
    }
};

const handleBlankOption = ({list, optionId, blankId}) => {
    const existing = list.find((item) => item.blankId === blankId);

    if (existing) {
        updateOrRemoveExisting({list, existing, optionId});
    } else if (optionId !== null) {
        list.push({blankId, optionId});
    }
};

const updateOrRemoveExisting = ({list, existing, optionId}) => {
    const shouldRemove = existing.optionId === optionId || optionId === null;

    if (shouldRemove) {
        list.splice(list.indexOf(existing), 1);
    } else {
        existing.optionId = optionId;
    }
};

const handleRegularOption = ({list, optionId, selected, questionId}) => {
    const index = list.indexOf(optionId);

    if (index === -1) {
        list.push(optionId);
    } else {
        list.splice(index, 1);
    }

    if (!list.length) delete selected[questionId];
};

const handleSingleSelect = ({selected, questionId, optionId, blankId}) => {
    if (blankId && !optionId) {
        delete selected[questionId];
        return;
    }

    if (blankId) {
        selected[questionId] = [{blankId, optionId}];
        return;
    }

    selected[questionId] = [optionId];
};

export const setBadges = (badges) => loyaltySlice.actions.setBadges(badges);

export const setCurrentBadge = (badgeType) => loyaltySlice.actions.setCurrentBadge(badgeType);

export const addMilestoneToBadge = (milestoneType, badgeType) =>
    loyaltySlice.actions.addMilestoneToBadge({milestoneType, badgeType});

export const addMilestonesToBadge = (milestones, badgeType) =>
    loyaltySlice.actions.addMilestonesToBadge({milestones, badgeType});

export const addSelectedRewardTypeToBadge = (rewardType, badgeType) =>
    loyaltySlice.actions.addSelectedRewardTypeToBadge({rewardType, badgeType});

export const setIsLoyaltyMockEnabled = (isLoyaltyMockEnabled) =>
    loyaltySlice.actions.setIsLoyaltyMockEnabled(isLoyaltyMockEnabled);

export const startQuiz = (quizType) => loyaltySlice.actions.startQuiz({quizType});

export const setRestoredQuizData = (quizType, quizData) =>
    loyaltySlice.actions.setRestoredQuizData({quizType, quizData});

export const finishQuiz = (quizType) => loyaltySlice.actions.finishQuiz({quizType});

export const resetQuiz = (quizType) => loyaltySlice.actions.resetQuiz({quizType});

export const setQuizAnswer = ({quizType, questionId, optionId, blankId, isMultipleSelect}) =>
    loyaltySlice.actions.setQuizAnswer({
        quizType,
        questionId,
        optionId,
        blankId,
        isMultipleSelect,
    });

export const setConversionStatus = (conversionStatus) => loyaltySlice.actions.setConversionStatus(conversionStatus);

export const setLoyaltyUserData = (userData) => loyaltySlice.actions.setLoyaltyUserData(userData);

export const setIsAnswersCorrect = ({quizType, isAnswersCorrect}) =>
    loyaltySlice.actions.setIsAnswersCorrect({quizType, isAnswersCorrect});

export const setIsTimeoutError = ({quizType, isTimeoutError}) =>
    loyaltySlice.actions.setIsTimeoutError({quizType, isTimeoutError});

export const setRewards = (rewards) => loyaltySlice.actions.setRewards(rewards);

export const setIsBadgesLoaded = (isBadgesLoaded) => loyaltySlice.actions.setIsBadgesLoaded(isBadgesLoaded);

export const setBadgesIsProgress = (isProgress) => loyaltySlice.actions.setBadgesIsProgress(isProgress);

export default loyaltySlice.reducer;
